/**
 * AlignmentOpportunityBand
 *
 * A contextual callout rendered between the KPI strip and the drill-down rail
 * on the Performance dashboard. It visualises the gap between:
 *   - Captured Incremental Revenue (CIR): already earned above misaligned baseline
 *   - Revenue Alignment Ceiling (RAC): what full alignment could earn
 *
 * Formula (from backend analytics_service.py):
 *   CIR = aligned_spend × (aligned_roas − misaligned_roas)
 *   RAC = total_spend  × (aligned_roas − misaligned_roas)
 *   Uncaptured = RAC − CIR
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MessageCircle, Zap } from 'lucide-react';
import { clsx } from 'clsx';
import { creativeWorkbenchApi, type WorkbenchKpiResponse } from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import type { ChatSeedInput } from './EmbeddedChatPanel';

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

interface AlignmentOpportunityBandProps {
  /**
   * Chat-analytics-ui Phase 4 — opens the EmbeddedChatPanel seeded with a
   * progress chart of the alignment opportunity.
   */
  onDiagnose?: (seed: ChatSeedInput) => void;
}

export function AlignmentOpportunityBand({ onDiagnose }: AlignmentOpportunityBandProps = {}) {
  const navigate = useNavigate();
  const { clientUid, personaId, dateRange } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchKpiResponse | null>(null);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    creativeWorkbenchApi
      .getPerformanceKpis({
        clientUid,
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
        personaId: personaId ?? null,
      })
      .then((p) => { if (!cancelled) setData(p); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [clientUid, personaId, dateRange.from, dateRange.to]);

  if (!data) return null;

  const { totals, tiles } = data;
  const cir = totals.captured_incremental_revenue ?? 0;
  const rac = totals.revenue_alignment_ceiling ?? 0;
  const uncaptured = totals.uncaptured_revenue ?? 0;

  // Don't render if there's no meaningful signal
  if (rac <= 0) return null;

  // Alignment rate comes from the tile value
  const alignedTile = tiles.find((t) => t.key === 'aligned_spend_pct');
  const alignmentPct = alignedTile?.value ?? 0;

  // Ceiling capture rate (% of RAC already realised)
  const capturePct = rac > 0 ? Math.min((cir / rac) * 100, 100) : 0;

  const steerabilityPerPct = totals.steerability_value_per_pct ?? 0;

  return (
    <div className={clsx(
      'rounded-xl border px-5 py-4',
      'bg-gradient-to-r from-teal-50/50 via-white to-white border-teal-200',
    )}>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        {/* Left: progress bar + copy */}
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={13} className="text-teal-500 flex-shrink-0" />
            <span className="text-[11px] font-bold text-teal-700 uppercase tracking-widest">
              Alignment Revenue Opportunity
            </span>
          </div>

          {/* Progress bar */}
          <div className="relative h-2 rounded-full bg-slate-100 overflow-hidden mb-2">
            <div
              className="absolute left-0 top-0 h-full rounded-full bg-teal-500 transition-all duration-700"
              style={{ width: `${capturePct}%` }}
            />
          </div>

          <p className="text-xs text-slate-600 leading-relaxed">
            You&apos;ve captured{' '}
            <span className="font-bold text-teal-700">{CURRENCY.format(cir)}</span> of your{' '}
            <span className="font-semibold text-navy-900">{CURRENCY.format(rac)} ceiling</span>
            {' '}— <span className="font-semibold">{alignmentPct.toFixed(0)}%</span> of spend is persona-aligned.{' '}
            {uncaptured > 0 && (
              <>
                Align the remaining spend to unlock{' '}
                <span className="font-bold text-teal-700">
                  {CURRENCY.format(uncaptured)} more
                </span>
                .
              </>
            )}
          </p>

          {steerabilityPerPct > 0 && (
            <p className="text-[10px] text-slate-400 mt-1 font-mono">
              Each 1% improvement in alignment ≈ {CURRENCY.format(steerabilityPerPct)} additional revenue
            </p>
          )}
        </div>

        {/* Right: stat summary */}
        <div className="flex items-center gap-6 flex-shrink-0">
          <Stat label="Captured" value={CURRENCY.format(cir)} accent="teal" />
          <div className="w-px h-8 bg-slate-200" />
          <Stat label="Uncaptured" value={CURRENCY.format(uncaptured)} accent="slate" />
          <div className="w-px h-8 bg-slate-200" />
          <Stat label="Ceiling" value={CURRENCY.format(rac)} accent="slate" />
        </div>

        {/* CTAs — split between navigation (full opportunity table) and chat */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate('/creative/performance/coverage')}
            className={clsx(
              'inline-flex items-center gap-1.5 text-[11px] font-semibold',
              'text-teal-700 border border-teal-300 bg-teal-50 rounded-full px-3 py-1.5',
              'hover:bg-teal-100 transition-colors',
            )}
          >
            View Opportunities
            <ArrowRight size={11} />
          </button>
          {onDiagnose && (
            <button
              onClick={() =>
                onDiagnose({
                  question: `I have ${CURRENCY.format(uncaptured)} in uncaptured revenue. Which personas have the biggest alignment gap and what should I do?`,
                  persona_id: personaId ?? undefined,
                  chart_axis: {
                    chart_type: 'progress',
                    metric: 'alignment',
                    date_range_days: 30,
                  },
                })
              }
              className={clsx(
                'inline-flex items-center gap-1.5 text-[11px] font-semibold',
                'text-teal-700 border border-teal-300 bg-white rounded-full px-3 py-1.5',
                'hover:bg-teal-50 transition-colors',
              )}
              title="Open the analyst chat with this opportunity pre-seeded"
            >
              <MessageCircle size={10} />
              Diagnose in Chat
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent: 'teal' | 'slate' }) {
  return (
    <div className="text-center">
      <div
        className={clsx(
          'text-lg font-bold tabular-nums',
          accent === 'teal' ? 'text-teal-700' : 'text-navy-900',
        )}
      >
        {value}
      </div>
      <div className="text-[10px] text-slate-400 uppercase tracking-wide font-mono">{label}</div>
    </div>
  );
}
